import React, { useState, useEffect, useRef } from "react";
import { adminAPI, backupAPI, securityAPI, vesselAPI, formatError } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Edit, X, Settings, TrendingUp, Download, Upload, RefreshCw, Loader, CheckCircle, AlertTriangle, Shield, Archive, Trash2 } from "lucide-react";
import { exportCSV, parseCSV } from "../utils/csvUtils";
import { fmtDateTime } from "../utils/dateUtils";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const CATEGORIES = ["Pulses", "Oils", "Vegetables", "Whole Spices", "Masalas", "Salt", "Vinegar", "Sweeteners", "Grains", "Rice", "Flour", "Others", "General"];

const tabs = [
  { id: "items",   label: "Master Item List",  icon: Settings  },
  { id: "prices",  label: "Cost Analysis",      icon: TrendingUp },
  { id: "backup",  label: "Backup & Restore",   icon: Download  },
  { id: "danger",  label: "Danger Zone",        icon: Trash2    },
];

function ImportCSVButton({ onImport }) {
  const ref = useRef();
  return (
    <>
      <button onClick={() => ref.current.click()}
        className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
        <Upload size={14} /> Import CSV
      </button>
      <input ref={ref} type="file" accept=".csv" className="hidden" onChange={e => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => onImport(parseCSV(ev.target.result));
        reader.readAsText(file);
        e.target.value = '';
      }} />
    </>
  );
}

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState("items");
  const [items, setItems] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [search, setSearch] = useState("");
  const [minStockModal, setMinStockModal] = useState(null); // { item }
  const [minStockValue, setMinStockValue] = useState("");
  const [importing, setImporting] = useState(false);

  useEffect(() => { fetchData(); }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "items") {
        const r = await adminAPI.getMasterItems({ active_only: false });
        setItems(r.data || []);
      } else {
        const r = await adminAPI.getTargetPrices();
        setPrices(r.data || []);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleSaveItem = async () => {
    setError("");
    try {
      if (modal.mode === "create") await adminAPI.createItem(form);
      else await adminAPI.updateItem(modal.id, form);
      setModal(null);
      setSuccess("Saved successfully!");
      setTimeout(() => setSuccess(""), 2000);
      fetchData();
    } catch (e) { setError(formatError(e)); }
  };

  const handleSavePrice = async () => {
    setError("");
    try {
      if (modal.mode === "create") await adminAPI.createTargetPrice(form);
      else await adminAPI.updateTargetPrice(modal.id, form);
      setModal(null);
      fetchData();
    } catch (e) { setError(formatError(e)); }
  };

  // Min stock: proper modal instead of prompt
  const openMinStock = (item) => {
    setMinStockValue(String(item.min_stock || 0));
    setMinStockModal(item);
  };

  const saveMinStock = async () => {
    if (!minStockModal) return;
    try {
      await adminAPI.updateMinStock(minStockModal.id, parseFloat(minStockValue) || 0);
      setMinStockModal(null);
      setSuccess(`Min stock updated for ${minStockModal.name}`);
      setTimeout(() => setSuccess(""), 2000);
      fetchData();
    } catch (e) { setError(formatError(e)); }
  };

  const handleToggleActive = async (item) => {
    try {
      await adminAPI.updateItem(item.id, { is_active: !item.is_active });
      fetchData();
    } catch (e) { alert(formatError(e)); }
  };

  const handleImportItems = async (rows) => {
    setImporting(true);
    let ok = 0, fail = 0;
    for (const row of rows) {
      try {
        await adminAPI.createItem({
          item_id: parseInt(row.item_id || row["#"] || 0),
          name: row.name || row["Item Name"] || "",
          category: row.category || row["Category"] || "Others",
          unit: row.unit || row["Unit"] || "Kg's",
          min_stock: parseFloat(row.min_stock || row["Min Stock"] || 0),
          can_issue_outside_menu: row.can_issue_outside_menu === "true" || row.can_issue_outside_menu === "Yes",
        });
        ok++;
      } catch { fail++; }
    }
    setImporting(false);
    setSuccess(`Imported ${ok} items${fail > 0 ? `, ${fail} failed (duplicates)` : ""}`);
    setTimeout(() => setSuccess(""), 4000);
    fetchData();
  };

  const handleImportPrices = async (rows) => {
    setImporting(true);
    let ok = 0;
    for (const row of rows) {
      try {
        await adminAPI.createTargetPrice({
          menu_item: row.menu_item || row["Menu Item"] || "",
          target_rate: parseFloat(row.target_rate || row["Target Rate"] || 0),
        });
        ok++;
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    }
    setImporting(false);
    setSuccess(`Imported ${ok} entries`);
    setTimeout(() => setSuccess(""), 3000);
    fetchData();
  };

  const filteredItems = items.filter(item =>
    (!filterCategory || item.category === filterCategory) &&
    (!search || item.name.toLowerCase().includes(search.toLowerCase()) || String(item.item_id).includes(search))
  );

  const nextId = items.length > 0 ? Math.max(...items.map(i => i.item_id || 0)) + 1 : 51;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Admin Settings</h1>
      </div>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}
      {importing && <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-lg px-4 py-2.5 text-sm text-[#D98032] animate-pulse">Importing...</div>}

      <div className="flex border-b border-[#E5E1DB]">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-1.5 transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div> : (
        <>
          {/* ---- MASTER ITEMS ---- */}
          {activeTab === "items" && (
            <>
              <div className="flex gap-3 flex-wrap items-center">
                <input
                  placeholder="Search by name or ID..."
                  className="flex-1 min-w-48 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                  value={search} onChange={e => setSearch(e.target.value)}
                />
                <select
                  className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                  value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div className="flex gap-2 ml-auto">
                  <ImportCSVButton onImport={handleImportItems} />
                  <button
                    onClick={() => exportCSV(filteredItems.map(i => ({ item_id: i.item_id, name: i.name, category: i.category, unit: i.unit, min_stock: i.min_stock, can_issue_outside_menu: i.can_issue_outside_menu })), "master_items")}
                    className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
                    <Download size={14} /> Export CSV
                  </button>
                  <button
                    onClick={() => { setForm({ item_id: nextId, unit: "Kg's" }); setModal({ type: "item", mode: "create" }); setError(""); }}
                    className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1">
                    <Plus size={14} /> Add Item
                  </button>
                </div>
              </div>

              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["#", "Item Name", "Category", "Unit", "Min Stock", "Perishable", "Can Issue Outside", "Active", "Actions"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {filteredItems.map(item => (
                        <tr key={item.id} className={`hover:bg-[#FAFAF8] ${!item.is_active ? "opacity-50" : ""}`}>
                          <td className="px-4 py-3 text-[#8A7E73]">{item.item_id}</td>
                          <td className="px-4 py-3 font-medium text-[#3A322C]">{item.name}</td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">{item.category}</span>
                          </td>
                          <td className="px-4 py-3 text-[#8A7E73]">{item.unit}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => openMinStock(item)}
                              className={`text-sm font-bold px-2 py-0.5 rounded transition-colors ${item.min_stock > 0 ? "text-[#637C64] bg-[#EDF2EE] hover:bg-[#E8956D]/10 hover:text-[#E8956D]" : "text-[#E8956D] hover:underline"}`}
                              data-testid={`min-stock-btn-${item.item_id}`}
                            >
                              {item.min_stock > 0 ? item.min_stock : "Set"}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            {item.is_perishable ? (
                              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#FDF4EB] text-[#D98032]">
                                {item.default_shelf_life_days}d
                              </span>
                            ) : <span className="text-xs text-[#C4BAAF]">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold ${item.can_issue_outside_menu ? "text-[#637C64]" : "text-[#C4BAAF]"}`}>
                              {item.can_issue_outside_menu ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleToggleActive(item)} className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.is_active ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
                              {item.is_active ? "Active" : "Inactive"}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => { setForm({ ...item }); setModal({ type: "item", mode: "edit", id: item.id }); setError(""); }} className="text-[#8A7E73] hover:text-[#E8956D]">
                              <Edit size={15} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {filteredItems.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8A7E73]">No items found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ---- COST ANALYSIS (Target Prices) ---- */}
          {activeTab === "prices" && (
            <>
              <div className="flex justify-end gap-2">
                <ImportCSVButton onImport={handleImportPrices} />
                <button
                  onClick={() => exportCSV(prices.map(p => ({ menu_item: p.menu_item, target_rate: p.target_rate })), "cost_analysis")}
                  className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]">
                  <Download size={14} /> Export CSV
                </button>
                <button
                  onClick={() => { setForm({}); setModal({ type: "price", mode: "create" }); setError(""); }}
                  className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1">
                  <Plus size={14} /> Add Target Price
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {prices.map(p => (
                  <div key={p.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-[#3A322C]">{p.menu_item}</p>
                        <p className="text-xl font-extrabold text-[#E8956D] mt-1">₹{p.target_rate} <span className="text-xs text-[#8A7E73] font-normal">per serving</span></p>
                      </div>
                      <button onClick={() => { setForm({ ...p }); setModal({ type: "price", mode: "edit", id: p.id }); setError(""); }} className="text-[#8A7E73] hover:text-[#E8956D]">
                        <Edit size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                {prices.length === 0 && <div className="col-span-3 text-center py-16 text-[#8A7E73]">No target prices set</div>}
              </div>
            </>
          )}
        </>
      )}

      {/* ---- MIN STOCK MODAL (replaces browser prompt) ---- */}
      {minStockModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-[#3A322C]">Set Minimum Stock</h2>
              <button onClick={() => setMinStockModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-[#5C5147]">
                Item: <strong>{minStockModal.name}</strong>
                <span className="text-[#8A7E73] ml-2">({minStockModal.unit})</span>
              </p>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Minimum Stock Level</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  autoFocus
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] focus:ring-2 focus:ring-[#E8956D]/20"
                  value={minStockValue}
                  onChange={e => setMinStockValue(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && saveMinStock()}
                  data-testid="min-stock-input"
                />
              </div>
              <p className="text-xs text-[#8A7E73]">An alert will be shown on the dashboard when stock falls below this level.</p>
              <div className="flex gap-3">
                <button onClick={() => setMinStockModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">Cancel</button>
                <button onClick={saveMinStock} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C] hover:bg-[#D48661]" data-testid="save-min-stock-btn">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "backup" && <BackupTab />}

      {activeTab === "danger" && <DangerZoneTab />}

      {modal?.type === "item" && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} Item`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Item ID *</label>
                <input type="number" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.item_id || ""} onChange={e => setForm({ ...form, item_id: parseInt(e.target.value) })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Unit *</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.unit || "Kg's"} onChange={e => setForm({ ...form, unit: e.target.value })}>
                  {["Kg's", "Litres", "No's", "Grams", "Packets"].map(u => <option key={u} value={u}>{u}</option>)}
                </select></div>
            </div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Item Name *</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Category *</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.category || ""} onChange={e => setForm({ ...form, category: e.target.value })}>
                <option value="">Select Category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Minimum Stock</label>
              <input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.min_stock || 0} onChange={e => setForm({ ...form, min_stock: parseFloat(e.target.value) || 0 })} /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.can_issue_outside_menu || false} onChange={e => setForm({ ...form, can_issue_outside_menu: e.target.checked })} />
              Can issue outside menu (General/Spares)
            </label>
            <div className="border-t border-[#E5E1DB] pt-3 space-y-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_perishable || false} onChange={e => setForm({ ...form, is_perishable: e.target.checked })} />
                <span className="font-semibold text-[#D98032]">Perishable Item</span>
                <span className="text-xs text-[#8A7E73]">(requires expiry date on inward)</span>
              </label>
              {form.is_perishable && (
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Default Shelf Life (days)</label>
                  <input type="number" min="1" placeholder="e.g. 30 for onions"
                    className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.default_shelf_life_days || ""} onChange={e => setForm({ ...form, default_shelf_life_days: parseInt(e.target.value) || 0 })} />
                  <p className="text-xs text-[#8A7E73] mt-1">Expiry date = inward date + shelf life days (auto-filled in stock inward)</p>
                </div>
              )}

              {/* Nutritional values per 100g */}
              <div className="pt-2 border-t border-[#F4F3F0]">
                <p className="text-xs font-bold uppercase text-[#8A7E73] mb-2">Nutritional Values (per 100g) — ICMR Reference</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "calories_per_100g", label: "Calories (kcal)" },
                    { key: "protein_per_100g",  label: "Protein (g)" },
                    { key: "carbs_per_100g",    label: "Carbs (g)" },
                    { key: "fat_per_100g",      label: "Fat (g)" },
                    { key: "iron_per_100g",     label: "Iron (mg)" },
                    { key: "calcium_per_100g",  label: "Calcium (mg)" },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="text-xs text-[#8A7E73] mb-0.5 block">{f.label}</label>
                      <input type="number" step="0.01" min="0" placeholder="0"
                        className="w-full border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D]"
                        value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: parseFloat(e.target.value) || 0 })} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSaveItem} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ---- PRICE MODAL ---- */}
      {modal?.type === "price" && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} Target Price`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Menu Item *</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.menu_item || ""} onChange={e => setForm({ ...form, menu_item: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Target Rate (₹ per serving) *</label>
              <input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.target_rate || ""} onChange={e => setForm({ ...form, target_rate: parseFloat(e.target.value) })} /></div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSavePrice} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── BACKUP & RESTORE TAB ────────────────────────────────────────────────────
function BackupTab() {
  const { user } = useAuth();
  const isSuperAdmin = user?.is_admin && ["Super Admin", "Administration", "Semi- Admin"].includes(user?.role_name || "");

  const [status, setStatus] = useState(null);
  const [runningBackup, setRunningBackup] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [restoreFile, setRestoreFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [diff, setDiff] = useState(null);
  const [selectedColls, setSelectedColls] = useState(new Set());
  const [confirmText, setConfirmText] = useState("");
  const [applying, setApplying] = useState(false);
  const fileRef = useRef();

  const loadStatus = async () => {
    try { const r = await backupAPI.status(); setStatus(r.data); }
    catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };
  useEffect(() => { loadStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runNow = async () => {
    setRunningBackup(true); setErr("");
    try { await backupAPI.runNow(); setMsg("Backup completed!"); loadStatus(); setTimeout(() => setMsg(""), 3000); }
    catch (e) { setErr(formatError(e)); }
    setRunningBackup(false);
  };

  const downloadBackup = (backupId) => {
    const token = localStorage.getItem("manna_access_token");
    const base = backupId
      ? `${backupAPI.download(backupId)}&token=${token}`
      : `${backupAPI.download(null)}?token=${token}`;
    window.open(base, "_blank");
  };

  const validateFile = async (file) => {
    if (!file) return;
    setRestoreFile(file); setDiff(null); setConfirmText(""); setErr("");
    setValidating(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await backupAPI.validate(fd);
      setDiff(r.data);
      setSelectedColls(new Set(Object.keys(r.data.diff || {})));
    } catch (e) { setErr(formatError(e)); setDiff(null); }
    setValidating(false);
  };

  const applyRestore = async () => {
    if (confirmText !== "RESTORE") { setErr("Type RESTORE to confirm"); return; }
    setApplying(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", restoreFile);
      const r = await backupAPI.applyRestore(fd, [...selectedColls].join(","));
      setMsg(r.data?.message || "Restore applied!");
      setDiff(null); setRestoreFile(null); setConfirmText(""); loadStatus();
    } catch (e) { setErr(formatError(e)); }
    setApplying(false);
  };

  const last = status?.last_backup;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="backup-restore-tab">
      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-3 text-sm flex items-center gap-2"><CheckCircle size={14}/>{msg}</div>}
      {err && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm">{err}<button onClick={() => setErr("")} className="ml-2">×</button></div>}

      {/* Status */}
      <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#637C64]/10 rounded-xl flex items-center justify-center"><Archive size={18} className="text-[#637C64]" /></div>
            <div>
              <p className="font-bold text-[#3A322C]">Master Data Backup</p>
              <p className="text-xs text-[#8A7E73]">Daily at 02:00 IST · 30 snapshots retained</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={loadStatus} className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] flex items-center gap-1.5">
              <RefreshCw size={13} />
            </button>
            <button onClick={runNow} disabled={runningBackup}
              className="bg-[#637C64] text-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-60 hover:bg-[#4F6450]">
              {runningBackup ? <Loader size={13} className="animate-spin" /> : <Archive size={13} />} Run Now
            </button>
          </div>
        </div>
        {last ? (
          <div className={`flex items-center gap-3 rounded-xl px-4 py-3 ${last.status === "success" ? "bg-[#EDF2EE]" : "bg-[#FBEAE7]"}`}>
            {last.status === "success" ? <CheckCircle size={16} className="text-[#637C64]" /> : <AlertTriangle size={16} className="text-[#C2624E]" />}
            <div>
              <p className={`text-sm font-bold ${last.status === "success" ? "text-[#637C64]" : "text-[#C2624E]"}`}>
                Last backup: {last.status}
              </p>
              <p className="text-xs text-[#8A7E73]">{fmtDateTime(last.created_at)} · {last.size_bytes ? `${Math.round(last.size_bytes/1024)}KB` : ""}</p>
            </div>
          </div>
        ) : <p className="text-sm text-[#8A7E73]">No backups yet.</p>}
      </div>

      {/* Backup list */}
      {(status?.backups || []).filter(b => b.status === "success" && b.filename).length > 0 && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB] flex items-center justify-between">
            <p className="font-bold text-[#3A322C] text-sm">Retained Snapshots</p>
            <button onClick={() => downloadBackup(null)}
              className="text-sm text-[#637C64] font-bold flex items-center gap-1 hover:underline">
              <Download size={13} /> Download Fresh Backup
            </button>
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-[#F4F3F0]">
            {status.backups.filter(b => b.status === "success" && b.filename).slice(0,30).map(b => (
              <div key={b.id} className="flex items-center px-4 py-2.5 hover:bg-[#FAFAF8]">
                <span className="flex-1 text-xs text-[#5C5147]">{fmtDateTime(b.created_at)}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full mr-3 ${b.type==="daily"?"bg-blue-100 text-blue-700":"bg-purple-100 text-purple-700"}`}>{b.type}</span>
                <span className="text-xs text-[#8A7E73] mr-3">{b.size_bytes ? `${Math.round(b.size_bytes/1024)}KB` : "—"}</span>
                <button onClick={() => downloadBackup(b.id)}
                  className="text-[#637C64] hover:text-[#3A7A3A] flex items-center gap-1 text-xs font-bold">
                  <Download size={12} /> Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Restore — Super Admin only */}
      {isSuperAdmin ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-[#D98032]" />
            <h3 className="font-bold text-[#3A322C]">Restore from Backup</h3>
            <span className="text-xs bg-[#FDF4EB] text-[#D98032] border border-[#D98032]/30 px-2 py-0.5 rounded font-bold">Super Admin Only</span>
          </div>
          <div
            className="border-2 border-dashed border-[#E5E1DB] rounded-xl p-6 text-center cursor-pointer hover:border-[#E8956D]/50"
            onClick={() => fileRef.current?.click()}>
            <Upload size={24} className="mx-auto mb-2 text-[#C4BAAF]" />
            <p className="text-sm font-bold text-[#5C5147]">{restoreFile ? restoreFile.name : "Upload a backup .zip"}</p>
            <p className="text-xs text-[#8A7E73] mt-0.5">Click to select · Only backups from this system</p>
            <input ref={fileRef} type="file" accept=".zip" className="hidden" onChange={e => e.target.files[0] && validateFile(e.target.files[0])} />
          </div>
          {validating && <div className="flex items-center gap-2 text-sm text-[#8A7E73]"><Loader size={13} className="animate-spin text-[#E8956D]" />Validating...</div>}
          {diff && (
            <div className="space-y-4">
              <div className="bg-[#F4F3F0] rounded-xl px-4 py-3 text-xs text-[#5C5147]">
                Backup from: <strong>{fmtDateTime(diff.manifest?.created_at)}</strong> · App v{diff.manifest?.app_version} · File: {diff.filename}
              </div>
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
                  <p className="font-bold text-sm text-[#3A322C]">Diff Preview — select collections to restore</p>
                  <p className="text-xs text-[#8A7E73]">Nothing is written until you confirm below.</p>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-[#F4F3F0]">
                  {Object.entries(diff.diff || {}).map(([coll, d]) => (
                    <label key={coll} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#FAFAF8]">
                      <input type="checkbox" className="accent-[#E8956D]"
                        checked={selectedColls.has(coll)}
                        onChange={e => { const n = new Set(selectedColls); e.target.checked ? n.add(coll) : n.delete(coll); setSelectedColls(n); }} />
                      <span className="font-mono text-xs font-bold text-[#3A322C] w-40 shrink-0">{coll}</span>
                      <div className="flex gap-3 text-xs flex-wrap">
                        {d.to_add > 0 && <span className="text-[#637C64] font-bold">+{d.to_add} to add</span>}
                        {d.to_update > 0 && <span className="text-blue-600 font-bold">~{d.to_update} to update</span>}
                        {d.live_only > 0 && <span className="text-[#D98032] font-bold">{d.live_only} live-only (flagged, not deleted)</span>}
                        {d.status === "missing_in_backup" && <span className="text-[#C4BAAF]">not in backup</span>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="bg-[#FBEAE7] border border-[#C2624E]/20 rounded-xl p-4 space-y-3">
                <p className="text-sm text-[#C2624E] font-bold">A pre-restore safety backup will be created automatically before applying.</p>
                <p className="text-xs text-[#C2624E]">Records in live DB but absent from backup will be flagged, NOT deleted.</p>
                <div>
                  <label className="text-xs font-bold uppercase text-[#C2624E] mb-1 block">Type RESTORE to confirm</label>
                  <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
                    className="w-full border border-[#C2624E]/30 rounded-lg px-3 py-2 text-sm font-mono outline-none"
                    placeholder="RESTORE" />
                </div>
                <button onClick={applyRestore} disabled={applying || confirmText !== "RESTORE" || selectedColls.size === 0}
                  className="bg-[#C2624E] text-white rounded-lg px-5 py-2.5 text-sm font-bold disabled:opacity-50 flex items-center gap-2">
                  {applying ? <Loader size={13} className="animate-spin" /> : <Shield size={13} />}
                  {applying ? "Applying..." : `Apply Restore (${selectedColls.size} collections)`}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-[#F4F3F0] rounded-xl px-5 py-4 flex items-center gap-3 text-sm text-[#8A7E73]">
          <Shield size={18} className="text-[#C4BAAF]" />
          Restore requires Super Admin or Administration role.
        </div>
      )}
    </div>
  );
}



// ─── DANGER ZONE TAB ─────────────────────────────────────────────────────────
function DangerZoneTab() {
  const { user } = useAuth();
  const isSuperAdmin = user?.is_admin;

  const [vesselConfirm, setVesselConfirm] = useState("");
  const [gateConfirm, setGateConfirm] = useState("");
  const [vesselResult, setVesselResult] = useState(null);
  const [gateResult, setGateResult] = useState(null);
  const [vesselLoading, setVesselLoading] = useState(false);
  const [gateLoading, setGateLoading] = useState(false);

  const handleVesselReset = async () => {
    if (vesselConfirm !== "DELETE ALL VESSEL LOGS") return;
    setVesselLoading(true); setVesselResult(null);
    try {
      const r = await vesselAPI.resetAll();
      setVesselResult({ ok: true, message: r.data.message });
      setVesselConfirm("");
    } catch (e) { setVesselResult({ ok: false, message: formatError(e) }); }
    setVesselLoading(false);
  };

  const handleGateReset = async () => {
    if (gateConfirm !== "DELETE ALL GATE ENTRIES") return;
    setGateLoading(true); setGateResult(null);
    try {
      const r = await securityAPI.resetAll();
      setGateResult({ ok: true, message: r.data.message });
      setGateConfirm("");
    } catch (e) { setGateResult({ ok: false, message: formatError(e) }); }
    setGateLoading(false);
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-[#FBEAE7] rounded-2xl px-6 py-8 text-center max-w-lg">
        <AlertTriangle size={32} className="mx-auto mb-3 text-[#C2624E]" />
        <p className="font-bold text-[#C2624E]">Super Admin only</p>
        <p className="text-sm text-[#8A7E73] mt-1">These actions require Super Admin / Administration role.</p>
      </div>
    );
  }

  const ResetCard = ({ title, desc, clears, untouched, confirmPhrase, confirmValue, setConfirmValue, onReset, loading, result }) => (
    <div className="bg-white border-2 border-[#C2624E]/30 rounded-2xl p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-[#FBEAE7] rounded-xl flex items-center justify-center flex-shrink-0">
          <Trash2 size={18} className="text-[#C2624E]" />
        </div>
        <div>
          <h3 className="font-bold text-[#3A322C] text-base">{title}</h3>
          <p className="text-sm text-[#8A7E73] mt-0.5">{desc}</p>
        </div>
      </div>
      <div className="bg-[#FDF4EB] rounded-xl px-4 py-3 text-xs space-y-1">
        <p className="font-bold text-[#D98032]">Clears: <span className="font-normal">{clears}</span></p>
        {untouched && <p className="font-bold text-[#637C64]">NOT cleared: <span className="font-normal">{untouched}</span></p>}
      </div>
      <div>
        <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1.5 block">
          Type exactly to enable
        </label>
        <input
          className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#C2624E] font-mono"
          placeholder={confirmPhrase}
          value={confirmValue}
          onChange={e => setConfirmValue(e.target.value)}
          data-testid={`confirm-input-${title.replace(/\s+/g,'-').toLowerCase()}`}
        />
        <p className="text-xs text-[#8A7E73] mt-1">Must type exactly: <code className="bg-[#F4F3F0] px-1 rounded font-bold text-[#C2624E]">{confirmPhrase}</code></p>
      </div>
      {result && (
        <div className={`text-sm px-4 py-3 rounded-xl ${result.ok ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
          {result.message}
        </div>
      )}
      <button
        onClick={onReset}
        disabled={loading || confirmValue !== confirmPhrase}
        className="w-full bg-[#C2624E] text-white rounded-xl py-3 font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-2 hover:bg-[#A5523F] transition-colors"
        data-testid={`reset-btn-${title.replace(/\s+/g,'-').toLowerCase()}`}
      >
        {loading ? <RefreshCw size={15} className="animate-spin" /> : <Trash2 size={15} />}
        {loading ? "Resetting…" : "Permanently Delete All — Cannot Be Undone"}
      </button>
    </div>
  );

  return (
    <div className="space-y-5 max-w-2xl" data-testid="danger-zone-tab">
      <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-xl px-4 py-3 flex items-start gap-3">
        <AlertTriangle size={16} className="text-[#C2624E] shrink-0 mt-0.5" />
        <p className="text-sm text-[#C2624E]">
          <strong>Danger Zone.</strong> These actions permanently delete live data and cannot be undone.
          Every reset is logged to the audit trail before deletion.
        </p>
      </div>

      <ResetCard
        title="Vessel Data Reset"
        desc="Deletes ALL vessel movement records. The vessel summary will show zero outstanding."
        clears="All vessel_logs (outward / inward / admin_correction movements)"
        untouched="vessel_inventory (opening stock), vessel_stock_logs (damage audit)"
        confirmPhrase="DELETE ALL VESSEL LOGS"
        confirmValue={vesselConfirm}
        setConfirmValue={setVesselConfirm}
        onReset={handleVesselReset}
        loading={vesselLoading}
        result={vesselResult}
      />

      <ResetCard
        title="Gate Log Reset"
        desc="Deletes ALL security/gate entries (food distribution, stores, visitor, staff, others). Entry log and today's count become zero."
        clears="All security_entries (all categories)"
        untouched={null}
        confirmPhrase="DELETE ALL GATE ENTRIES"
        confirmValue={gateConfirm}
        setConfirmValue={setGateConfirm}
        onReset={handleGateReset}
        loading={gateLoading}
        result={gateResult}
      />
    </div>
  );
}
